<template>
  <section class="section-base form-section-base form-base">
    <div class="form-section-base__title">
      Сведения о заявителе, являющимся заинтересованным лицом
    </div>
    <v-container>
      <div class="form-section-base__controls">
        <v-row>
          <v-col>
            <v-radio-group v-model="role" row>
              <v-radio
                label="Физическое лицо"
                :value="Role.PERSON"
                :disabled="roleType !== Role.PERSON"
              />
              <v-radio
                label="Юридическое лицо"
                :value="Role.ORGANIZATION"
                :disabled="isOrganizationDisabled"
              />
              <v-radio
                label="Иностранное юридическое лицо"
                :value="Role.FOREIGN_ORGANIZATION"
                :disabled="isForeignOrganizationDisabled"
              />
            </v-radio-group>
          </v-col>
        </v-row>
      </div>

      <MrpaEnableAssigneePerson
        v-if="role === Role.PERSON"
        :v="$v.person"
        :disabled="disabled"
      />

      <MrpaEnableAssigneeOrganization
        v-if="role === Role.ORGANIZATION"
        :v="$v.organization"
        :disabled="disabled"
      />

      <MrpaEnableAssigneeForeignOrganization
        v-if="role === Role.FOREIGN_ORGANIZATION"
        :v="$v.foreignOrganization"
        :disabled="disabled"
      />

      <Footer
        @next-btn-click="btnSaveClickHandler(StepDirection.NEXT)"
        @prev-btn-click="btnSaveClickHandler(StepDirection.PREV)"
        :loading-next="loadingNext"
      />
    </v-container>
  </section>
</template>

<script>
import cloneDeep from 'clone-deep';
import deepmerge from 'deepmerge';
import { Role } from '@/utils/dictionaries';
import { checkInvalid, leaveStep } from '@/utils/services';
import { ServicesModule } from '@/store/modules/services';
import Footer from '@/components/services/elements/Footer';
import { StepDirection, StepName } from '@/types/services';
import MrpaEnableAssigneePerson, {
  mrpaEnableAssigneePersonModel,
  getMrpaEnableAssigneePersonValidation,
} from './MrpaEnableAssigneePerson';
import MrpaEnableAssigneeOrganization, {
  mrpaEnableAssigneeOrganizationModel,
  getMrpaEnableAssigneeOrganizationValidation,
  prefillEsiaMrpaEnableAssigneeOrganization,
} from './MrpaEnableAssigneeOrganization';
import MrpaEnableAssigneeForeignOrganization, {
  mrpaEnableAssigneeForeignOrganizationModel,
  getMrpaEnableAssigneeForeignOrganizationValidation,
  prefillEsiaMrpaEnableAssigneeForeignOrganization,
} from './MrpaEnableAssigneeForeignOrganization';
import { prefillEsiaAssignee } from '@/components/services/EgrnConfirm/Applicant/components/Assignee';

export default {
  name: 'MrpaEnableAssignee',
  props: {
    loadingNext: {
      type: Boolean,
      required: true,
    },
    roleType: {
      type: String,
      required: true,
    },
    disabled: {
      type: Boolean,
    },
  },
  components: {
    MrpaEnableAssigneePerson,
    MrpaEnableAssigneeOrganization,
    MrpaEnableAssigneeForeignOrganization,
    Footer,
  },
  data() {
    return {
      StepDirection,
      leaveStep,
      StepName,
      Role,
      role: this.roleType,
      invalidFields: [],
      person: cloneDeep(mrpaEnableAssigneePersonModel),
      organization: cloneDeep(mrpaEnableAssigneeOrganizationModel),
      foreignOrganization: cloneDeep(
        mrpaEnableAssigneeForeignOrganizationModel
      ),
    };
  },
  computed: {
    isOrganizationDisabled() {
      return this.roleType !== Role.ORGANIZATION;
    },

    isForeignOrganizationDisabled() {
      return ![Role.FOREIGN_ORGANIZATION, Role.ORGANIZATION].includes(
        this.roleType
      );
    },
  },
  validations() {
    return {
      person: getMrpaEnableAssigneePersonValidation(this.person),
      organization: getMrpaEnableAssigneeOrganizationValidation(
        this.organization
      ),
      foreignOrganization: getMrpaEnableAssigneeForeignOrganizationValidation(
        this.foreignOrganization
      ),
    };
  },
  created() {
    this.prefillDraft();
  },
  methods: {
    touchFields() {
      this.invalidFields = [];

      this.$v[this.role].$touch();
      checkInvalid(this.$v[this.role], this.invalidFields);
    },

    async prefillDraft() {
      const applicantStore = cloneDeep(
        ServicesModule.statement.subjects.applicant
      );

      if (
        !applicantStore.person &&
        !applicantStore.organization &&
        !applicantStore.foreignOrganization
      ) {
        await this.prefillEsia();
        return;
      }

      if (applicantStore.person) {
        this.role = Role.PERSON;

        this.person = deepmerge.all([
          mrpaEnableAssigneePersonModel,
          applicantStore.person,
          {
            isTrustee: true,
            assignee:
              applicantStore.assignee ?? mrpaEnableAssigneePersonModel.assignee,
          },
        ]);
      }

      if (applicantStore.organization) {
        this.role = Role.ORGANIZATION;
        this.organization = deepmerge.all([
          mrpaEnableAssigneeOrganizationModel,
          applicantStore.organization,
          {
            assignee: applicantStore.assignee,
          },
        ]);
      }

      if (applicantStore.foreignOrganization) {
        this.role = Role.FOREIGN_ORGANIZATION;
        this.foreignOrganization = deepmerge.all([
          mrpaEnableAssigneeForeignOrganizationModel,
          applicantStore.foreignOrganization,
          {
            assignee: applicantStore.assignee,
          },
        ]);
      }
    },

    async prefillEsia() {
      if (this.roleType === Role.PERSON) {
        await this.prefillEsiaPerson();
      } else if (this.roleType === Role.ORGANIZATION) {
        await Promise.all([
          this.prefillEsiaOrganization(),
          this.prefillEsiaForeignOrganization(),
        ]);
      } else if (this.roleType === Role.FOREIGN_ORGANIZATION) {
        await this.prefillEsiaForeignOrganization();
      }
    },

    async prefillEsiaPerson() {
      const assignee = await prefillEsiaAssignee();

      this.person = deepmerge(mrpaEnableAssigneePersonModel, {
        assignee,
      });
    },

    async prefillEsiaOrganization() {
      this.organization = await prefillEsiaMrpaEnableAssigneeOrganization();
    },

    async prefillEsiaForeignOrganization() {
      this.foreignOrganization = await prefillEsiaMrpaEnableAssigneeForeignOrganization();
    },

    assemblyModel() {
      const applicant = {};

      applicant[this.role] = cloneDeep(this[this.role]);
      delete applicant[this.role].assignee;

      if (this.role === Role.PERSON) {
        applicant.assignee = cloneDeep(this.person.assignee);

        return applicant;
      }

      applicant.assignee = cloneDeep(this[this.role]).assignee;

      return applicant;
    },

    btnSaveClickHandler(direction) {
      leaveStep({
        stepThis: this,
        direction,
        step: StepName.S2,
      });
    },
  },
};
</script>
